import { useEffect, useRef, useState } from "react";
import { Avatar, Button, Card, Input, Typography, message as antdMessage } from "antd";
import { RobotOutlined, SendOutlined, UserOutlined, CheckOutlined, CloseOutlined } from "@ant-design/icons";
import {
  AiAgentChatResponse,
  PendingAiAgentAction,
  useAiAgentChat,
  useCancelAiAgentAction,
  useConfirmAiAgentAction,
} from "../services/aiagent.service";

const { Text, Title } = Typography;
const { TextArea } = Input;

const PAGE_BG = "#f5f6fa";
const CARD_BG = "#ffffff";
const CARD_BORDER = "#e8eaf0";
const ACCENT = "#6366f1";
const TEXT_PRIMARY = "#1e293b";

type ChatRole = "user" | "assistant";

type ChatMessage = {
  id: string;
  role: ChatRole;
  text: string;
  pendingActions?: PendingAiAgentAction[];
  isThinking?: boolean;
};

const THINKING_ID = "thinking";

const TypingIndicator = () => (
  <span style={{ display: "inline-flex", gap: 4, alignItems: "center", padding: "4px 0" }}>
    <style>{`
      @keyframes ai-agent-typing-bounce {
        0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
        40% { transform: scale(1); opacity: 1; }
      }
    `}</style>
    {[0, 1, 2].map((i) => (
      <span
        key={i}
        style={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          background: "#94a3b8",
          animation: "ai-agent-typing-bounce 1.2s infinite ease-in-out",
          animationDelay: `${i * 0.15}s`,
        }}
      />
    ))}
  </span>
);

type ActionState = "pending" | "confirming" | "confirmed" | "cancelling" | "cancelled" | "failed";

const WELCOME_MESSAGE: ChatMessage = {
  id: "welcome",
  role: "assistant",
  text:
    "Hi, I'm the Tripattendynt admin assistant. Ask me about trips, travellers, attendants, finances, or analytics. " +
    "I can also propose actions like cancelling a trip or refunding a payment -- you'll always get a chance to confirm before anything happens.",
};

const AiAgentDashboard = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([WELCOME_MESSAGE]);
  const [input, setInput] = useState("");
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [actionStates, setActionStates] = useState<Record<string, ActionState>>({});
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  const chatMutation = useAiAgentChat();
  const confirmMutation = useConfirmAiAgentAction();
  const cancelMutation = useCancelAiAgentAction();

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const appendMessage = (msg: ChatMessage) => setMessages((prev) => [...prev, msg]);

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed || chatMutation.isLoading) return;

    appendMessage({ id: `user-${Date.now()}`, role: "user", text: trimmed });
    appendMessage({ id: THINKING_ID, role: "assistant", text: "", isThinking: true });
    setInput("");

    chatMutation.mutate(
      { conversationId, message: trimmed },
      {
        onSuccess: (res: any) => {
          if (res?.statusCode !== 200 && res?.statusCode !== 201) {
            setMessages((prev) => prev.filter((m) => m.id !== THINKING_ID));
            antdMessage.error(res?.statusMessage || "The AI agent could not process that request.");
            return;
          }
          const data: AiAgentChatResponse = res.data;
          setConversationId(data.conversationId);
          setMessages((prev) => [
            ...prev.filter((m) => m.id !== THINKING_ID),
            {
              id: `assistant-${Date.now()}`,
              role: "assistant",
              text: data.reply,
              pendingActions: data.pendingActions,
            },
          ]);
        },
        onError: (error: any) => {
          setMessages((prev) => prev.filter((m) => m.id !== THINKING_ID));
          antdMessage.error(error?.response?.statusMessage || "Something went wrong reaching the AI agent.");
        },
      }
    );
  };

  const handleConfirm = (action: PendingAiAgentAction) => {
    setActionStates((prev) => ({ ...prev, [action.actionId]: "confirming" }));
    confirmMutation.mutate(
      { actionId: action.actionId },
      {
        onSuccess: (res: any) => {
          if (res?.statusCode === 200 || res?.statusCode === 201) {
            setActionStates((prev) => ({ ...prev, [action.actionId]: "confirmed" }));
            antdMessage.success(res?.statusMessage || "Action confirmed and executed.");
          } else {
            setActionStates((prev) => ({ ...prev, [action.actionId]: "failed" }));
            antdMessage.error(res?.statusMessage || "Could not execute that action.");
          }
        },
        onError: (error: any) => {
          setActionStates((prev) => ({ ...prev, [action.actionId]: "failed" }));
          antdMessage.error(error?.response?.statusMessage || "Could not execute that action.");
        },
      }
    );
  };

  const handleCancel = (action: PendingAiAgentAction) => {
    setActionStates((prev) => ({ ...prev, [action.actionId]: "cancelling" }));
    cancelMutation.mutate(
      { actionId: action.actionId },
      {
        onSuccess: () => {
          setActionStates((prev) => ({ ...prev, [action.actionId]: "cancelled" }));
        },
        onError: (error: any) => {
          setActionStates((prev) => ({ ...prev, [action.actionId]: "failed" }));
          antdMessage.error(error?.response?.statusMessage || "Could not cancel that action.");
        },
      }
    );
  };

  return (
    <div style={{ background: PAGE_BG, minHeight: "calc(100vh - 64px)", padding: 24, display: "flex", flexDirection: "column" }}>
      <Title level={4} style={{ color: TEXT_PRIMARY, marginBottom: 16 }}>
        AI Agent
      </Title>

      <Card
        style={{ flex: 1, display: "flex", flexDirection: "column", borderRadius: 12, border: `1px solid ${CARD_BORDER}`, background: CARD_BG }}
        styles={{ body: { flex: 1, display: "flex", flexDirection: "column", padding: 0, height: "70vh" } }}
      >
        <div style={{ flex: 1, overflowY: "auto", padding: 24 }}>
          {messages.map((msg) => (
            <div
              key={msg.id}
              style={{
                display: "flex",
                flexDirection: msg.role === "user" ? "row-reverse" : "row",
                alignItems: "flex-start",
                gap: 10,
                marginBottom: 18,
              }}
            >
              <Avatar
                icon={msg.role === "user" ? <UserOutlined /> : <RobotOutlined />}
                style={{ backgroundColor: msg.role === "user" ? ACCENT : "#1a1a2e", flexShrink: 0 }}
              />
              <div style={{ maxWidth: 640 }}>
                <div
                  style={{
                    background: msg.role === "user" ? ACCENT : PAGE_BG,
                    color: msg.role === "user" ? "#fff" : TEXT_PRIMARY,
                    borderRadius: msg.role === "user" ? "16px 4px 16px 16px" : "4px 16px 16px 16px",
                    padding: "10px 14px",
                    whiteSpace: "pre-wrap",
                    lineHeight: 1.5,
                  }}
                >
                  {msg.isThinking ? <TypingIndicator /> : msg.text}
                </div>

                {msg.pendingActions?.map((action) => {
                  const state = actionStates[action.actionId] ?? "pending";
                  return (
                    <div
                      key={action.actionId}
                      style={{
                        marginTop: 8,
                        border: `1px solid ${CARD_BORDER}`,
                        borderRadius: 10,
                        padding: 14,
                        background: "#fffbea",
                      }}
                    >
                      <Text strong style={{ display: "block", marginBottom: 8 }}>
                        Action awaiting confirmation
                      </Text>
                      <Text style={{ display: "block", marginBottom: 12 }}>{action.summary}</Text>

                      {state === "pending" && (
                        <div style={{ display: "flex", gap: 8 }}>
                          <Button
                            size="small"
                            type="primary"
                            icon={<CheckOutlined />}
                            style={{ background: ACCENT, border: "none" }}
                            onClick={() => handleConfirm(action)}
                          >
                            Confirm
                          </Button>
                          <Button size="small" icon={<CloseOutlined />} onClick={() => handleCancel(action)}>
                            Cancel
                          </Button>
                        </div>
                      )}
                      {state === "confirming" && <Text type="secondary">Executing...</Text>}
                      {state === "cancelling" && <Text type="secondary">Cancelling...</Text>}
                      {state === "confirmed" && <Text type="success">Confirmed and executed.</Text>}
                      {state === "cancelled" && <Text type="secondary">Cancelled -- no changes made.</Text>}
                      {state === "failed" && <Text type="danger">Something went wrong. Try again.</Text>}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        <div style={{ borderTop: `1px solid ${CARD_BORDER}`, padding: 16, display: "flex", gap: 10 }}>
          <TextArea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onPressEnter={(e) => {
              if (!e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder="Ask about trips, travellers, attendants, finance, or analytics..."
            autoSize={{ minRows: 1, maxRows: 4 }}
            disabled={chatMutation.isLoading}
          />
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={handleSend}
            loading={chatMutation.isLoading}
            disabled={!input.trim()}
            style={{ background: ACCENT, border: "none" }}
          >
            Send
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default AiAgentDashboard;
